
export type IssueReportingChatMessage = UserMessage | AssistantMessage | UserPhoto | AssistantIssueCreated | AssistantYesNoQuestion | UserYesNoResponse

export interface UserMessage {
    id: string,
    type: 'userMessage',
    content: string
}

export interface AssistantMessage {
    id: string,
    type: 'assistantMessage',
    content: string
}

export interface UserPhoto {
    id: string,
    type: 'userPhoto',
    photoId: string
    fileKey: string,
    src: string
}

export interface AssistantIssueCreated {
    id: string,
    type: 'assistantIssueCreated',
    title: string,
    description: string,
    location: string,
    photoIds: string[],
    completed: boolean
}

export interface AssistantYesNoQuestion {
    id: string,
    type: 'assistantYesNoQuestion',
    question: string
}

export interface UserYesNoResponse {
    id: string,
    type: 'userYesNoResponse',
    response: 'yes' | 'no'
}

export type IssueReportingChat = {
    language: string,
    messages: IssueReportingChatMessage[]
}

export type IssueReportingResponseChunk = AssistantMessage | AssistantIssueCreated | AssistantYesNoQuestion

export const completeIssueReportingChat = async (
        siteId: string,
        chat: IssueReportingChat,
        onChunk: (chunk: IssueReportingResponseChunk) => void) => {
    const res = await fetch(
        `/api/public/site/${encodeURIComponent(siteId)}/issue-reporting/chat`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // Ask specifically for an event-stream so the server keeps the
                // connection open and flushes with   `\n\n`.
                Accept: 'text/event-stream'
            },
            // NB: you may want credentials / auth headers here as well
            body: JSON.stringify(chat)
        }
    );

    if (!res.body) {
        throw new Error('No response body');
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';


    while (true) {
        const {value, done} = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, {stream: true});

        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
            for (const line of frame.split('\n')) {
                const trimmed = line.trimStart();
                if (trimmed.startsWith('data:')) {
                    const payload = trimmed.slice(5).trim(); // remove 'data:' prefix
                    if (!payload) continue;

                    try {
                        const chunk = JSON.parse(payload) as IssueReportingResponseChunk;
                        onChunk(chunk);
                    } catch (err) {
                        console.warn('Failed to parse SSE chunk', err, payload);
                    }
                }
            }
        }
    }

    // Handle anything that was left in the buffer when the stream closed.
    if (buffer.trim()) {
        const maybeDataLine = buffer
            .split('\n')
            .find(line => line.trimStart().startsWith('data:'));
        if (maybeDataLine) {
            try {
                const chunk = JSON.parse(
                    maybeDataLine.trimStart().slice(5).trim()
                ) as IssueReportingResponseChunk;
                onChunk(chunk);
            } catch {
                /* ignore */
            }
        }
    }
}